// frontend/src/tour/steps/leads-map.steps.js

// Helper function to normalize role names
function normalizeRole(role) {
  if (!role) return "Sales Rep";
  const roleLower = role.toLowerCase();
  if (roleLower.includes("admin")) return "Admin";
  if (roleLower.includes("director")) return "Admin";
  if (roleLower.includes("manager")) return "Manager";
  return "Sales Rep";
}

export const LEADS_MAP_TOUR_STEPS = (role) => {
  const normalizedRole = normalizeRole(role);
  
  const steps = [
    // Step 1: Welcome / Map Overview
    {
      id: "leads-map-welcome",
      selector: "body",
      placement: "center",
      copy: {
        Admin: {
          title: "Leads Map",
          content:
            "This interactive map shows all your leads geographically. Uploaded leads are visualized as colored pins on the map. Each pin represents a lead, and colors indicate their status.",
        },
        Manager: {
          title: "Leads Map",
          content:
            "View all leads on an interactive map. Each colored pin represents a lead, with colors indicating their status. This helps you see geographic coverage and plan routes.",
        },
        "Sales Rep": {
          title: "Leads Map",
          content:
            "See all your leads plotted on a map. Each colored pin is a lead - click them to add to your route. Colors show the lead's status.",
        },
      },
    },

    // Step 2: Map Container - Lead Visualization
    {
      id: "leads-map-container",
      selector: '[data-tour="leads-map-container"]',
      placement: "top",
      copy: {
        Admin: {
          title: "Lead Visualization",
          content:
            "All uploaded leads are displayed as colored pins on this map. Each pin's color represents the lead's status (New, Follow Up, Closed, etc.). Click on any pin to view lead details and add it to a route.",
        },
        Manager: {
          title: "Lead Visualization",
          content:
            "Every lead you upload appears as a colored pin on the map. Pin colors indicate status - blue for new leads, orange for follow-ups, green for closed. Click pins to see details and build routes.",
        },
        "Sales Rep": {
          title: "Your Leads on the Map",
          content:
            "All your leads show up as colored pins here. Click any pin to see the lead's details and add them to your route. The colors tell you the lead's status at a glance.",
        },
      },
    },

    // Step 3: Map filters
    {
      id: "leads-map-filter-button",
      selector: '[data-tour="leads-map-filter-button"]',
      placement: "left",
      copy: {
        Admin: {
          title: "Map filters",
          content:
            "Open filters to narrow pins by territory, lead status, and upload date. Active filters show a count on this button. Use Clear All inside the panel to reset.",
        },
        Manager: {
          title: "Map filters",
          content:
            "Filter the map by territory, status, and date so your team sees the right leads. The badge shows how many filters are active.",
        },
        "Sales Rep": {
          title: "Map filters",
          content:
            "Filter which leads appear on the map—by status, territory, or when they were added—so you can focus on the work that matters today.",
        },
      },
    },

    // Step 4: Geocode missing leads
    {
      id: "leads-map-geocode-button",
      selector: '[data-tour="leads-map-geocode-button"]',
      placement: "top",
      copy: {
        Admin: {
          title: "Geocode missing leads",
          content:
            "Run this when leads lack map coordinates. The system looks up addresses and places pins. Run again if some rows still need geocoding.",
        },
        Manager: {
          title: "Geocode missing leads",
          content:
            "Use this to geocode leads that are not showing on the map yet. It processes missing addresses in batches until pins appear.",
        },
        "Sales Rep": {
          title: "Geocode missing leads",
          content:
            "If a lead does not appear on the map, tap here to geocode missing addresses so you can see and route them.",
        },
      },
    },

    // Step 5: Box select
    {
      id: "leads-map-box-select-button",
      selector: '[data-tour="leads-map-box-select-button"]',
      placement: "top",
      copy: {
        Admin: {
          title: "Box select",
          content:
            "Turn on Box Select, then click and drag on the map to highlight many leads at once. A short hint appears above the route table: click and drag on the map to select multiple leads.",
        },
        Manager: {
          title: "Box select",
          content:
            "Enable Box Select, then draw a rectangle on the map to grab a group of leads quickly. The helper text above the table reminds you to click and drag while box select is on.",
        },
        "Sales Rep": {
          title: "Box select",
          content:
            "Tap Box Select, then click and drag on the map to outline an area and select every pin inside. You'll see a reminder above the route table while box select mode is active.",
        },
      },
    },

    // Step 6: Route Builder Table
    {
      id: "route-builder-table",
      selector: '[data-tour="route-builder-table"]',
      placement: "top",
      copy: {
        Admin: {
          title: "Route Builder Table",
          content:
            "This table shows all leads you've selected for your route. Click pins on the map to add them here. The table displays lead name, address, status, and notes. You can remove leads or reorder them. Once you have 2+ leads, you can save the route.",
        },
        Manager: {
          title: "Route Builder Table",
          content:
            "As you click pins on the map, they appear in this table. This is your route builder - it shows all selected leads with their details. Remove leads you don't need, then save your optimized route when ready.",
        },
        "Sales Rep": {
          title: "Build Your Route",
          content:
            "Click pins on the map to add leads to this table. This shows all the stops in your route. You can see addresses, status, and remove leads you don't need. Once you have at least 2 leads, you can save your route.",
        },
      },
    },

    // Step 4: Saved Routes Button
    {
      id: "saved-routes-button",
      selector: '[data-tour="saved-routes-button"]',
      placement: "top",
      copy: {
        Admin: {
          title: "Saved Routes",
          content:
            "Click this button to view and manage all your saved routes. You can access previously created routes, edit them, or create new ones. Saved routes help you plan efficient daily schedules.",
        },
        Manager: {
          title: "Saved Routes",
          content:
            "Access all your saved routes here. View routes you've created, see which leads are included, and reuse routes for efficient planning. This saves time when planning similar routes.",
        },
        "Sales Rep": {
          title: "Your Saved Routes",
          content:
            "Click here to see all your saved routes. You can view routes you've created before, see the leads included, and reuse them for your daily planning. This makes it easy to repeat successful routes.",
        },
      },
    },

    // Step 5: Lead Detail Drawer — intro
    {
      id: "desktop-lead-drawer-intro",
      selector: "body",
      placement: "center",
      copy: {
        Admin: {
          title: "Lead Detail Drawer",
          content:
            "Click any pin on the map to open the Lead Detail Drawer — a side panel that slides in from the right. View the lead's name, address, status, stats, and notes without ever leaving the map.",
        },
        Manager: {
          title: "Lead Detail Drawer",
          content:
            "Tap any map pin to open the Lead Detail Drawer on the right side. It shows the lead's key info and lets you make edits directly — no need to navigate away from the map.",
        },
        "Sales Rep": {
          title: "Lead Detail Drawer",
          content:
            "Click a pin on the map and a drawer slides open on the right with that lead's details. You can read notes, check status, and update info all from the map.",
        },
      },
    },

    // Step 6: Lead Detail Drawer — edit & save
    {
      id: "desktop-lead-drawer-edit",
      selector: "body",
      placement: "center",
      copy: {
        Admin: {
          title: "Edit Lead Inline",
          content:
            "The drawer includes edit fields for name, contact info, status, and notes — always visible, no extra clicks needed. Make your changes and hit Save. Updates sync across the CRM instantly.",
        },
        Manager: {
          title: "Edit Lead Inline",
          content:
            "Scroll down in the drawer to see the edit fields. Update the lead's name, email, phone, status, or notes and press Save. Changes are applied immediately without leaving the map.",
        },
        "Sales Rep": {
          title: "Edit Lead Inline",
          content:
            "Scroll down in the drawer to update the lead's info — name, phone, status, notes. Hit Save when done. You stay on the map the whole time.",
        },
      },
    },

    // Step 7: Add to Route from Drawer
    {
      id: "desktop-drawer-add-route",
      selector: "body",
      placement: "center",
      copy: {
        Admin: {
          title: "Add to Route from Drawer",
          content:
            "At the bottom of the Lead Detail Drawer is the 'Add to Route' button. Tap it to add the lead to your route builder. The drawer closes automatically and the lead appears in the table below.",
        },
        Manager: {
          title: "Add to Route from Drawer",
          content:
            "Use the 'Add to Route' button at the bottom of the drawer to queue that lead for your route. The drawer closes and the lead is added to the route builder table.",
        },
        "Sales Rep": {
          title: "Add to Route from Drawer",
          content:
            "See the 'Add to Route' button at the bottom of the drawer? Tap it and that lead is added to your route. The drawer closes so you can keep clicking more pins.",
        },
      },
    },

    // Step 8: Route Builder — View button
    {
      id: "route-builder-view-btn",
      selector: '[data-tour="route-builder-view-btn"]',
      placement: "top",
      copy: {
        Admin: {
          title: "View Lead from Route Table",
          content:
            "Each lead in the route builder has a View button. Click it to reopen that lead's detail drawer and make edits — without removing it from the route.",
        },
        Manager: {
          title: "View Lead from Route Table",
          content:
            "Click View next to any lead in the route table to open its detail drawer. You can update info and come back to the route without losing your progress.",
        },
        "Sales Rep": {
          title: "View Lead from Route Table",
          content:
            "Use the View button next to a lead in your route to pull up its details. Edit anything you need, then close the drawer and continue building your route.",
        },
      },
    },
  ];

  // Resolve steps based on role
  return steps.map((step) => {
    const roleCopy = step.copy[normalizedRole] || step.copy["Sales Rep"];
    
    return {
      selector: step.selector,
      placement: step.placement,
      title: roleCopy.title,
      content: roleCopy.content,
    };
  });
};

/**
 * Versioned "new only" tour for Leads Map (desktop).
 * Add steps here when you ship new features; bump the key (e.g. v1_7_leads_map_new_complete) when you add more.
 */
export const LEADS_MAP_NEW_STEPS = () => [];